<template>
  <bm-tooltip top>
    <template #activator="{ on }">
      <v-btn
        :class="classes"
        :style="btnStyle"
        depressed
        v-bind="$attrs"
        :loading="loading || (showActionState && success)"
        :text="buttonType.toLowerCase() === 'ghost'"
        :outlined="buttonType.toLowerCase() === 'secondary'"
        :data-qa="dataQaVal"
        exact
        v-on="tooltipVal ? on : undefined"
        @click="clickHandler"
      >
        <template #loader>
          <!-- Standard v-btn behaviour-->
          <v-progress-circular
            v-if="!success"
            :indeterminate="true"
            :size="23"
            :width="2"
          ></v-progress-circular>
          <bm-icon v-if="success" icon="Check" color="#fff" />
        </template>
        <slot>{{ successMessage || labelVal }}</slot>
      </v-btn>
    </template>
    <span v-if="tooltipVal">{{ tooltipVal }}</span>
  </bm-tooltip>
</template>

<script setup lang="ts">
import BmTooltip from "@/components/controls/input/BmTooltip.vue";
import { useBmLabelled } from "@/components/controls/input/mixins/BmLabelled";
import { computed } from "vue";
import type Key from "@/app/types/common/key";
import { makeRef } from "@/components/composables/reactivityTypes";
import { theme } from "@/app/theme";

export interface BmBtnProps {
  buttonType?: string;
  showActionState?: boolean;
  btnClass?: string;
  btnStyle?: object | string;
  successMessage?: string;
  loading?: boolean;

  // BmLabelled
  label?: string;
  tooltip?: string;
  t?: Key;
  dataQa?: object | string;
}

export interface BmBtnEvents {
  (event: "click", value: MouseEvent): void;
}

const props = withDefaults(defineProps<BmBtnProps>(), {
  buttonType: "primary",
  showActionState: false,
  loading: false,
});

const emit = defineEmits<BmBtnEvents>();

const success = makeRef(false);

const { dataQaVal, tooltipVal, labelVal } = useBmLabelled(() => props);

const classes = computed(() => {
  const classes = ["bm-btn"];

  if (props.btnClass) {
    classes.push(props.btnClass);
  }

  if (props.showActionState && success.value) {
    classes.push("bm-btn-success");
  }

  if (props.buttonType.toLowerCase() === "primary") {
    classes.push("primaryButton", "white--text");
  } else if (props.buttonType.toLowerCase() === "primary-danger") {
    classes.push("primary-danger");
  } else if (props.buttonType.toLowerCase() === "primary-success") {
    classes.push("primary-success");
  } else if (props.buttonType.toLowerCase() === "primary-warn") {
    classes.push("primary-warn");
  } else if (props.buttonType.toLowerCase() === "ghost") {
    classes.push("ghost", "primary--text");
  } else if (props.buttonType.toLowerCase() === "secondary") {
    classes.push(
      ...(theme.isDark()
        ? ["secondarybtn-dark"]
        : ["secondarybtn", "primary--text"])
    );
  } else if (props.buttonType.toLowerCase() === "largebtn") {
    classes.push("largebtn", "primary--text");
  } else if (props.buttonType.toLowerCase() === "largebtn-dotted") {
    classes.push("largebtn-dotted", "primary--text");
  } else if (props.buttonType.toLowerCase() === "largebtn-primary-border") {
    classes.push("largebtn-primary-border", "primary--text");
  } else if (props.buttonType.toLowerCase() === "ai") {
    classes.push("ai-btn");
  } else {
    throw new Error(
      "Unrecognized bm-btn type: " + props.buttonType.toLowerCase()
    );
  }

  if (props.successMessage) {
    classes.push("not-clickable");
    classes.push("bm-btn-success");
  }

  return classes;
});

const clickHandler = (event: MouseEvent) => {
  emit("click", event);

  if (props.showActionState) {
    success.value = true;
    setTimeout(() => {
      success.value = false;
    }, 2000);
  }
};
</script>

<script lang="ts">
// This standard script block is used for component options
export default {
  name: "BmBtn",
  inheritAttrs: false,
};
</script>

<style scoped lang="sass">
@import "@/sass/typography"
@import "@/sass/colours"

.bm-btn
  font-family: $secondary-font-family
  text-transform: none !important // Allows the text to be camel cased

.primary-danger
  color: #fff !important
  background-color: $bm-color-error-medium !important

.primary-success
  color: #FFF !important
  background-color: $bm-color-success-medium !important

.primary-warn
  color: $bm-color-text01 !important
  background-color: $bm-color-warning-medium !important

.secondarybtn
  border: 2px solid !important
  background-color: $bm-color-background01 !important

.secondarybtn-dark
  border: 2px solid $bm-color-brand02 !important
  background-color: $bm-color-background01 !important

.secondarybtn-dark .v-btn__content
  color: $bm-color-text01

.largebtn
  border: 1px solid !important
  background-color: $bm-color-background01 !important
  height: 70px !important

.largebtn-dotted
  border: 1px dotted !important
  background-color: $bm-color-background01 !important
  height: 70px !important

.v-application .bm-btn-success
  background-color : $bm-color-success-medium !important
  transition: background-color 0.25s ease !important

.not-clickable
  pointer-events: none

.largebtn-primary-border
  border: 2px solid $bm-color-brand01 !important
  height: 60px !important

.ai-btn
  background: linear-gradient(to right, #002E5D, #18A0FB)
  color: #FFFFFF !important
  border: none !important

@media print
  .bm-btn
    display: none

:deep(.v-btn__content)
  width: 100%
</style>
